import { Button, Divider, Grid, Tooltip, Typography } from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import React, { memo, useEffect } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { FiberManualRecordOutlined } from '@mui/icons-material';
import Transition from '../common/Transition';
import moment from 'moment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Header } from '../common/Header';
import { GetPredictionDetails, handleTogglePredictionDetailsDialog } from '../../store/prediction-slice/prediction';

const PredictionDetailsDialog = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { 
        predictionDetailsLoading, 
        predictionDetails, 
        predictionDetailsDialog 
    } = useAppSelector((state: RootState) => state.prediction);

    function handleClose() {
        dispatch(handleTogglePredictionDetailsDialog({ isOpen: false, body: null }))
    }

    useEffect(() => {
        if(predictionDetailsDialog && predictionDetailsDialog.isOpen)
            dispatch(GetPredictionDetails(predictionDetailsDialog?.body?.id));
    }, [predictionDetailsDialog])

    return(
        <Dialog
            fullWidth
            maxWidth="lg"
            open={predictionDetailsDialog.isOpen}
            TransitionComponent={Transition}
            keepMounted
            onClose={(_, reason) => {
                if(reason !== 'backdropClick') handleClose();
            }}
            aria-describedby="alert-dialog-slide-description"
        >
            <Header title={t('PREDICTION_DETAILS_TITLE', { name: predictionDetailsDialog?.body?.name })} />
            <DialogContent>
                { !predictionDetails || predictionDetailsLoading ? 
                <Grid container p={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    { predictionDetailsLoading ? 
                        <Box sx={{ width: '100%' }}>
                            <LinearProgress />
                        </Box>
                    : !predictionDetails ? 
                        <Typography variant={'h6'}>{t('NO_PREDICTION_DETAILS')}</Typography>
                    : '' }
                </Grid> : 
                <React.Fragment>
                    <Grid container sx={{ display: 'flex', flexDirection: 'row'}} spacing={10}>
                        <Grid item xs={6}>
                            <Grid mb={1} item style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <HelpOutlineIcon />
                                <Typography fontWeight="bold" ml={1} variant='h6'>{t('PREDICTION_DETAILS_REQUEST')}</Typography>
                            </Grid>
                            <Divider />
                            <Grid mt={1.5} item xs={12}>
                                <Grid container display={"flex"} justifyContent={"space-between"}>
                                    <Grid item fontWeight="bold">{t('PREDICTION_RESULT_NAME')}</Grid>
                                    <Grid item>{predictionDetails?.request?.name ?? '---'}</Grid>
                                </Grid>
                            </Grid>
                            <Grid item mt={1.5} xs={12}><Divider /></Grid>
                            <Grid container mt={1.5} display={"flex"} flexDirection={"column"}>
                                <Grid item xs={12}>
                                    <Grid item xs={12} fontWeight="bold">
                                        {t('PREDICTION_INPUTS')}
                                    </Grid>
                                    <Grid item xs={12} sx={{ height: '199px', overflowY: 'auto' }}>
                                        { predictionDetails?.request?.inputColumns?.map((el : any, index: number) => {
                                            return(
                                                <Grid key={index} item xs={12} pr={1}>
                                                    <Grid container display={"flex"} justifyContent={"space-between"}>
                                                        <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row'}}>
                                                            <FiberManualRecordOutlined fontSize='small' />
                                                            <Typography ml={.5}>
                                                                {el.name} → {el.value}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            )
                                        })}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={6}>
                            <Grid mb={1} container display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                                <Grid item display="flex" flexDirection="row" alignItems="center">
                                    <QuestionAnswerIcon />
                                    <Typography fontWeight="bold" ml={1} variant='h6'>{t('PREDICTION_DETAILS_RESPONSE')}</Typography>
                                </Grid>
                                <Tooltip title={
                                    <React.Fragment>
                                        <Grid container display={"flex"} justifyContent={"space-between"} spacing={1}>
                                            <Grid item fontWeight="bold">{t('PREDICTION_DETAILS_START_DATE_TIME')}</Grid>
                                            <Grid item>{moment(predictionDetails?.response?.startPredictDateTimeUTC).format('DD/MM/YYYY hh:mm')}</Grid>
                                        </Grid>
                                        <Grid container display={"flex"} justifyContent={"space-between"} spacing={1}>
                                            <Grid item fontWeight="bold">{t('PREDICTION_DETAILS_END_DATE_TIME')}</Grid>
                                            <Grid item>{moment(predictionDetails?.response?.endPredictDateTimeUTC).format('DD/MM/YYYY hh:mm')}</Grid>
                                        </Grid>
                                        <Grid container display={"flex"} justifyContent={"space-between"} spacing={1}>
                                            <Grid item fontWeight="bold">{t('PREDICTION_DETAILS_DURATION')}</Grid>
                                            <Grid item>{predictionDetails?.response?.predictDuration}</Grid>
                                        </Grid>
                                    </React.Fragment>
                                }>
                                    <Grid item display="flex" flexDirection="row" alignItems="center">
                                        <AccessTimeIcon />
                                        <Typography ml={1}>{predictionDetails?.response?.predictDuration}</Typography>
                                    </Grid>
                                </Tooltip>
                            </Grid>
                            <Grid container display={"flex"} flexDirection={"column"} spacing={1}>
                                <Grid item xs={12}><Divider /></Grid>
                                <Grid item xs={12} fontWeight="bold">
                                    {t('PREDICTION_RESULTS')}
                                </Grid>
                                <Grid item xs={12} sx={{ height: '200px', overflowY: 'auto' }}>
                                    { predictionDetails?.response?.predictionResult?.map((el: string, index: number) => {
                                        return(
                                            <Grid key={index} item xs={12} pr={1}>
                                                <Grid container display={"flex"} justifyContent={"space-between"}>
                                                    <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row'}}>
                                                        <Typography>&nbsp;- {el}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        )
                                    })}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </React.Fragment>
                }
            </DialogContent>
            <DialogActions>
                <Button className="button-text-color" onClick={handleClose}>{t('CLOSE_BUTTON')}</Button>
            </DialogActions> 
        </Dialog>
    )
}

export default memo(PredictionDetailsDialog);